import { Box, Typography, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { AutocompleteInput, ReferenceInput, SelectInput, useListContext, required, useDataProvider, useNotify, useTranslate } from 'react-admin';
import { Growth } from '@/components/analytics/common/Growth.jsx';
import { Link } from 'react-router-dom';
import { ProductProgressBar } from '@/components/analytics/product/ProductAnalyticsInfo.jsx';
import PropTypes from 'prop-types';
import { rankingTypeChoices } from '@/components/global/options/selectOptions.jsx';
import { useForm, FormProvider } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints.js';
import CircularProgress from '@mui/material/CircularProgress';
import { calcDaysFromSeriesData } from '@/components/analytics/PeriodFilters.jsx';
import { analyticsStyles } from '@/themes/commonStyles/analytics.js';
import { formatNumberWithComma } from '@/helpers/format/formatNumber.js';
import { Palette } from '@/themes/themeOverrides/Palette';
import { createFilterToQuery } from '@/helpers/filters/filterToQuery.js';
import { buildListFilterUrl } from '@/helpers/filters/buildListFilterUrl.js';
import { useTagCategoryNameSource, useTagNameSource } from '@/i18n/hooks.js';

const Title = () => {
    const translate = useTranslate();
    return <Typography variant='h6'>{translate('analytics.tag.ranking')}</Typography>;
};

const StyledElement = styled(Box)(() => ({
    ...analyticsStyles.element,
}));

const TagItem = ({ item, totalProducts }) => {
    const nameSource = useTagNameSource();
    const percentage = (item.current / totalProducts) * 100;

    return (
        <Box className='item' backgroundColor={item.categoryColor}>
            <Box className='name'>{item[nameSource]}</Box>
            <Box className='percentage'>{formatNumberWithComma(percentage, 1)}%</Box>
        </Box>
    );
};

const Element = ({ item, total, days }) => {
    const translate = useTranslate();
    const tagNameSource = useTagNameSource();
    const categoryNameSource = useTagCategoryNameSource();
    const percentage = (item.current / total) * 100;
    const growth = ((item.current - item.previous) / item.previous) * 100;
    const daysStr = days === 1 ? translate('analytics.common.daySingular') : translate('analytics.common.daysWithCount', { count: days });
    const transactionsFilter = { 'transactionContainers->product->tags->id_in': [item.id] };
    const transactionsLink = buildListFilterUrl('/transaction', transactionsFilter, ['transactionContainers->product->tags->id_in']);

    return (
        <StyledElement className='element element--tag'>
            <Box className='element-header'>
                <Box className='element-left'>
                    <Typography variant='caption'>
                        <strong>#{item[tagNameSource]}</strong>
                    </Typography>
                    <Typography className='element-category' variant='caption'>
                        {item[categoryNameSource]}
                    </Typography>
                </Box>
                <Box className='element-right'>
                    <Tooltip
                        arrow
                        placement='top'
                        classes={{ tooltip: 'tooltip-white tooltip-white--small' }}
                        title={
                            <span>
                                {translate('analytics.product.collectedProductsCountLabel')} <br />
                                <strong>{daysStr}</strong>
                            </span>
                        }
                        PopperProps={{
                            modifiers: [
                                {
                                    name: 'offset',
                                    options: {
                                        offset: [0, -15],
                                    },
                                },
                            ],
                        }}
                    >
                        <Link style={{ color: Palette.black, textDecoration: 'none' }} to={transactionsLink}>
                            <Typography variant='caption'>{item.current}</Typography>
                        </Link>
                        <Growth value={growth} className='growth--small' />
                    </Tooltip>
                </Box>
                {isFinite(percentage) ? (
                    <Typography variant='caption' className='percentage'>
                        {formatNumberWithComma(percentage, 1)}%
                    </Typography>
                ) : null}
            </Box>
            <ProductProgressBar percentage={percentage} />
        </StyledElement>
    );
};

const StyledTagRanking = styled(Box)(({ theme }) => ({
    padding: '40px 40px 48px',
    borderRadius: '16px',
    backgroundColor: Palette.white,
    [`@media (max-width: ${breakpoints.values.md}px)`]: {
        padding: '24px',
    },
    '& .ranking-header': {
        ...analyticsStyles.rankingHeader,
    },
    '& .ranking-content': {
        marginTop: '36px',
        [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
            marginTop: '20px',
        },
    },
    '& .ranking-footer': {
        ...analyticsStyles.rankingFooter,
    },
    '& .element + .element': {
        marginTop: '17px',
    },
}));

const StyledProductTagRanking = styled(Link)(() => ({
    ...analyticsStyles.productTagRanking,
}));

export const TagRanking = ({ productPage = false }) => {
    const defaultRankingFilters = { rankingType: 'mostPopular', categoryId: null };
    const { data, filterValues } = useListContext();
    const form = useForm({ defaultValues: defaultRankingFilters });
    const { watch } = form;
    const rankingFilterValues = watch();
    const notify = useNotify();
    const translate = useTranslate();
    const nameSource = useTagNameSource();

    const dataProvider = useDataProvider();
    const filter = { ...filterValues, ...rankingFilterValues };
    const {
        data: rankingData,
        isFetching,
        error,
    } = useQuery({
        queryKey: ['tag-ranking', { filter }],
        queryFn: () => dataProvider.summary('analytic-products/tag-ranking', { filter }),
    });

    if (!data || data.length === 0) return null;

    if (error) {
        notify(error.message, { type: 'error' });
        return null;
    }

    if (productPage) {
        let ranking = rankingData?.data?.data ?? [];
        const fullRanking = ranking;
        ranking = ranking.slice(0, 10);

        const totalProducts = fullRanking.reduce((sum, item) => sum + item.current, 0);
        const midpoint = Math.floor(ranking.length / 2);
        const firstRowTags = ranking.slice(0, midpoint);
        const secondRowTags = ranking.slice(midpoint);

        return (
            <StyledProductTagRanking to='/analytic-products' className='product-tag-ranking'>
                <Box>
                    <Typography variant='caption'>{translate('analytics.common.popular')}</Typography>
                    <Box className='tag-list-wrapper'>
                        {isFetching ? (
                            <CircularProgress />
                        ) : (
                            <Box className='tag-list'>
                                <Box className='tag-list-row'>
                                    {firstRowTags.map(item => (
                                        <TagItem key={item.id} item={item} totalProducts={totalProducts} />
                                    ))}
                                </Box>
                                <Box className='tag-list-row'>
                                    {secondRowTags.map(item => (
                                        <TagItem key={item.id} item={item} totalProducts={totalProducts} />
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            </StyledProductTagRanking>
        );
    } else {
        const analytic = data.find(s => s.id === 'current').data;
        const series = data.find(s => s.id === 'series').data;
        const days = calcDaysFromSeriesData(series);
        const ranking = rankingData?.data?.data ?? [];

        return (
            <StyledTagRanking>
                <Box className='ranking-header'>
                    <Title />
                    <Box className='ranking-header__filters'>
                        <FormProvider {...form}>
                            <SelectInput
                                source='rankingType'
                                choices={rankingTypeChoices(translate)}
                                fullWidth
                                label={false}
                                emptyText={translate('common.all')}
                                validate={required()}
                            />
                            <Box className='autocomplete-parent' sx={{ width: 'auto' }}>
                                <ReferenceInput source='categoryId' reference='tag-category' perPage={250}>
                                    <AutocompleteInput
                                        emptyText={translate('analytics.common.allCategories')}
                                        optionText={nameSource}
                                        filterToQuery={createFilterToQuery([nameSource])}
                                        label={false}
                                        fullWidth
                                        disablePortal={true}
                                    />
                                </ReferenceInput>
                            </Box>
                        </FormProvider>
                    </Box>
                </Box>
                <Box className='ranking-content'>
                    {isFetching ? <CircularProgress /> : ranking.map(item => <Element key={item.id} item={item} total={analytic.packagesCount} days={days} />)}
                </Box>
            </StyledTagRanking>
        );
    }
};

Element.propTypes = {
    item: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        nameEn: PropTypes.string.isRequired,
        categoryId: PropTypes.string.isRequired,
        categoryName: PropTypes.string.isRequired,
        categoryNameEn: PropTypes.string.isRequired,
        previous: PropTypes.number.isRequired,
        current: PropTypes.number.isRequired,
    }).isRequired,
    total: PropTypes.number.isRequired,
    days: PropTypes.number.isRequired,
};

TagItem.propTypes = {
    item: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        nameEn: PropTypes.string.isRequired,
        categoryColor: PropTypes.string,
        current: PropTypes.number.isRequired,
    }).isRequired,
    totalProducts: PropTypes.number.isRequired,
};
